import React, { useEffect, useState, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Button, message, Table, Tag,
  Typography, Row, Switch, Select,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { DATE_FORMAT } from '../packageUtil';
import { legalDocumentTypes } from './LegalDocumentUtil';
import './LegalDocuments.css';
import { selectLegalDocuments } from '../../../../state/domain/legalDocuments/legalDocumentReducer';
import { requestLegalDocuments, requestSaveLegalDocument } from '../../../../state/ui/configuration/bynx/legalDocumentUiActions';
import routeLocations from '../../../../utility/route-locations';
import { selectSaveLegalDocumentRequest, selectLoadLegalDocumentsRequest } from '../../../../state/ui/configuration/bynx/legalDocumentUiReducer';

const { Column } = Table;
const { Text } = Typography;

// TABLE
function DocumentsTable({
  isLoading, documents, toggleActive, activeLoading, editableDocument, currentDocumentType,
}) {
  const [visibleDocuments, setVisibleDocuments] = useState([]);

  const intl = useIntl();
  const RowTotal = () => (
    <div>
      <Text>{`${visibleDocuments.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  useEffect(() => {
    const tempDocuments = documents.slice();
    setVisibleDocuments(tempDocuments.map(document => ({
      ...document,
      displayDate: moment.utc(document.effectiveDate).format(DATE_FORMAT),
    })));
  }, [documents]);

  const onActiveUpdate = (record) => {
    // Only toggle if the package from date is in the future
    if (moment.utc().isBefore(moment.utc(record.effectiveDate))) toggleActive(record);
    else message.warn(intl.formatMessage({ id: 'futureDate' }));
  };


  // Gets the versionNumber of the document currently being used
  const currentDocumentNumber = useMemo(() => {
    const activeDocuments = visibleDocuments.slice().filter(doc => doc.active && moment.utc(doc.effectiveDate).isBefore());
    const activeDocument = activeDocuments.find(
      doc => !activeDocuments.find(
        laterDoc => doc.versionNumber < laterDoc.versionNumber,
      ),
    );
    return activeDocument ? activeDocument.versionNumber : null;
  }, [visibleDocuments]);

  function sortByFromDate(a, b) {
    const adate = moment.utc(a.effectiveDate);
    const bdate = moment.utc(b.effectiveDate);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return 0;
  }

  function sortByVersion(a, b) {
    if (a.versionNumber === b.versionNumber) return 0;
    if (a.versionNumber < b.versionNumber) return -1;
    return 1;
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visibleDocuments}
      rowKey={record => record.versionNumber}
    >

      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        width={120}
        render={(active, record) => {
          const editable = editableDocument ? editableDocument.versionNumber === record.versionNumber : false;
          if (editable) {
            return (
              <Row type="flex" justify="center">
                <Switch loading={activeLoading} onChange={() => onActiveUpdate(record)} size="small" checked={active} />
              </Row>
            );
          }
          return (
            <Row type="flex" justify="center">
              {record.versionNumber === currentDocumentNumber ? (
                <Tag color="green"><FormattedMessage id="active" /></Tag>
              ) : <Tag color="volcano"><FormattedMessage id="published" /></Tag>}
            </Row>
          );
        }}
      />
      <Column
        title={<FormattedMessage id="version" />}
        width="10%"
        dataIndex="versionNumber"
        key="versionNumber"
        sorter={sortByVersion}
        defaultSortOrder="descend"
      />
      <Column
        sorter={sortByFromDate}
        title={<FormattedMessage id="startDate" />}
        width="70%"
        dataIndex="displayDate"
        key="displayDate"
      />
      <Column
        className="via-action-column"
        align="right"
        width={70}
        render={(text, record) => {
          const editable = editableDocument ? editableDocument.versionNumber === record.versionNumber : false;
          return (
            <Row>
              {editable ? (
                <Link to={`${routeLocations.admin_legal_documents}/edit/${currentDocumentType}`}>
                  <Button size="small" type="primary" icon="edit" style={{ marginBottom: '0.6em' }}>
                    <FormattedMessage id="edit" />
                  </Button>
                </Link>
              ) : <></>}
              <Link to={`${routeLocations.legal}/${record.documentType}/${record.versionNumber}`} target="_blank">
                <Button size="small" type="primary" icon="eye">
                  <FormattedMessage id="view" />
                </Button>
              </Link>
            </Row>
          );
        }}
      />
    </Table>
  );
}

DocumentsTable.propTypes = {
  isLoading: PropTypes.bool,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      documentType: PropTypes.string.isRequired,
      versionNumber: PropTypes.number.isRequired,
      effectiveDate: PropTypes.string.isRequired,
      content: PropTypes.string,
    }),
  ),
  currentDocumentType: PropTypes.string.isRequired,
  editableDocument: PropTypes.shape({
    documentType: PropTypes.string.isRequired,
    versionNumber: PropTypes.number.isRequired,
    effectiveDate: PropTypes.string.isRequired,
    content: PropTypes.string,
  }),
  toggleActive: PropTypes.func.isRequired,
  activeLoading: PropTypes.bool,
};

DocumentsTable.defaultProps = {
  isLoading: false,
  documents: [],
  activeLoading: false,
  editableDocument: null,
};

function LegalDocuments({
  isLoading, loadPageData, selectDocuments,
  saveActive, activeLoading,
}) {
  // Load documents on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);
  // Defaults to terms of use, entirely arbitrary.
  const [currentDocumentType, setCurrentDocumentType] = useState(legalDocumentTypes.TERMS);

  const intl = useIntl();
  // Grab the documents relating to the selected document type from the state
  const documents = useMemo(() => selectDocuments(currentDocumentType) || undefined, [selectDocuments, currentDocumentType]);

  const editableDocument = documents
    ? documents.find(document => !document.active || moment.utc(document.effectiveDate).isAfter(moment.utc()))
    : undefined;

  // Only one editable document at a time. So only present the create button when there isn't a document already in an unused state
  const CreateButton = () => {
    if (editableDocument) return <></>;
    return (
      <Link to={`${routeLocations.admin_legal_documents}/edit/${currentDocumentType}`}>
        <Button type="primary" loading={isLoading} icon="plus">
          <FormattedMessage id="create" />
        </Button>
      </Link>
    );
  };

  // Simply puts in an update request. Since only the editable document can be toggled,
  // it does nothing if you are somehow toggling a different record.
  const toggleActive = (record) => {
    if (record.versionNumber === editableDocument.versionNumber) {
      saveActive({ ...editableDocument, active: !editableDocument.active }, undefined, intl);
    }
  };

  return (
    <ConfigHeading
      titleId="legalDocuments"
      actions={<CreateButton />}
    >
      <Row>
        <FormattedMessage id="documentType" />
        :&nbsp;
        <Select value={currentDocumentType} onChange={setCurrentDocumentType} className="document-type-selector">
          <Select.Option value={legalDocumentTypes.TERMS} key={legalDocumentTypes.TERMS}><FormattedMessage id="termsOfUse" /></Select.Option>
          <Select.Option value={legalDocumentTypes.PRIVACY} key={legalDocumentTypes.PRIVACY}><FormattedMessage id="privacyPolicy" /></Select.Option>
          <Select.Option value={legalDocumentTypes.EULA} key={legalDocumentTypes.EULA}><FormattedMessage id="endUserLicence" /></Select.Option>
        </Select>
      </Row>
      <DocumentsTable
        isLoading={isLoading}
        documents={documents}
        editableDocument={editableDocument}
        toggleActive={toggleActive}
        activeLoading={activeLoading}
        currentDocumentType={currentDocumentType}
      />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
LegalDocuments.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  saveActive: PropTypes.func.isRequired,
  activeLoading: PropTypes.bool,
  selectDocuments: PropTypes.func.isRequired,
};

LegalDocuments.defaultProps = {
  isLoading: false,
  activeLoading: false,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const toggleActiveRequest = selectSaveLegalDocumentRequest(state);
  const loadingRequest = selectLoadLegalDocumentsRequest(state);

  return {
    isLoading: loadingRequest ? loadingRequest.isLoading : false,
    selectDocuments: docType => selectLegalDocuments(state, docType),
    activeLoading: toggleActiveRequest ? toggleActiveRequest.isLoading : false,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestLegalDocuments()),
  saveActive: (document, history, intl) => dispatch(requestSaveLegalDocument(document, history, intl)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(LegalDocuments));
